import { MAP_TYPES, MapType } from '../components/map/helpers/mapTypes';
import { Mission, Sample } from '../db';
import { Coordinate } from 'ol/coordinate';
import { polygon, point } from '@turf/helpers';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { alertConfirm } from '../alertDispatcher';
import { SampleSkipper } from './SampleSkipper';
import { dispatchMissionUpdated } from '../missionEvents';
import { dispatchPathUpdated, dispatchSamplesUpdated } from '../mapEvents';

export const skipSampleAction = { name: 'Skip', description: 'Skip samples inside the selected area' };
export const unskipSampleAction = { name: 'Unskip', description: 'Unskip samples inside the selected area' };
export const removeSampleAction = {
  name: 'Remove',
  description: 'Remove samples inside the selected area. Will not remove samples with barcodes',
};
export const clearBarcodeAction = {
  name: 'Clear Barcode',
  description: 'Clear barcodes from samples inside the selected area',
};
export const moveSampleAction = {
  name: 'Move',
  description: 'Move samples inside the selected area to a new location',
};
export const cancelSampleAction = { name: 'Cancel', description: 'Cancel the current lasso action' };

const lassoSampleActions = [
  skipSampleAction,
  unskipSampleAction,
  removeSampleAction,
  clearBarcodeAction,
  moveSampleAction,
  cancelSampleAction,
];

export type LassoSampleAction = (typeof lassoSampleActions)[number];

export interface MoveActionInfo {
  action: 'Move';
  samplesWithCentroids: Array<{
    sample: Sample;
    centroid: { lat: number; lon: number; displace: (latOffset: number, lonOffset: number) => Promise<void> };
  }>;
  originalCenter: Coordinate;
}

class LassoService {
  private static instance: LassoService;

  private constructor() {
    if (LassoService.instance) {
      throw new Error('LassoService is a singleton. Use LassoService.getInstance() instead.');
    }
  }

  public static getInstance(): LassoService {
    if (!LassoService.instance) {
      LassoService.instance = new LassoService();
    }

    return LassoService.instance;
  }

  getPossibleSampleActions(mapType: MapType): LassoSampleAction[] {
    switch (mapType) {
      case MAP_TYPES.MAPS:
        return [
          skipSampleAction,
          unskipSampleAction,
          removeSampleAction,
          clearBarcodeAction,
          moveSampleAction,
          cancelSampleAction,
        ];
      case MAP_TYPES.SAMPLING:
        return [skipSampleAction, unskipSampleAction, cancelSampleAction];
      default:
        return [cancelSampleAction];
    }
  }

  /**
   * Handle the selected lasso action on samples within a polygon
   * @param action The action to perform
   * @param mission The mission containing samples
   * @param polygonPointsLatLon Polygon coordinates in [lat, lon] format
   * @param addCheckpoint Function to add a checkpoint for undo functionality
   * @returns For Move action, returns MoveActionInfo with samples and center; otherwise void
   */
  public async handleLassoAction(
    action: LassoSampleAction,
    mission: Mission | undefined,
    polygonPointsLatLon: Coordinate[][],
    addCheckpoint: (name: string) => void,
  ): Promise<MoveActionInfo | undefined> {
    if (!mission) {
      return;
    }

    switch (action.name) {
      case 'Skip':
        addCheckpoint('Skip samples inside zone');
        await this.skipSamplesInsidePolygon(mission, polygonPointsLatLon);
        break;

      case 'Unskip':
        addCheckpoint('Unskip samples inside zone');
        await this.unskipSamplesInsidePolygon(mission, polygonPointsLatLon);
        break;

      case 'Remove':
        addCheckpoint('Remove samples inside zone');
        await this.removeSamplesInsidePolygon(mission, polygonPointsLatLon);
        break;

      case 'Clear Barcode':
        addCheckpoint('Clear barcodes inside zone');
        await this.clearBarcodesInsidePolygon(mission, polygonPointsLatLon);
        break;

      case 'Move':
        return await this.prepareMoveAction(mission, polygonPointsLatLon);

      case 'Cancel':
        // Do nothing
        break;

      default:
        console.warn(`Unknown lasso action: ${action}`);
    }
  }

  /**
   * Complete the move action by applying the calculated offset to all samples
   * @param moveInfo The move information from handleLassoAction
   * @param newCenter The new center position for the samples
   * @param addCheckpoint Function to add a checkpoint
   */
  public async completeMoveAction(
    moveInfo: MoveActionInfo,
    newCenter: Coordinate,
    addCheckpoint: (name: string) => void,
  ): Promise<void> {
    console.log('Completing move action to new center:', newCenter);
    addCheckpoint('Move samples inside zone');

    const latOffset = newCenter[0] - moveInfo.originalCenter[0];
    const lonOffset = newCenter[1] - moveInfo.originalCenter[1];

    // Move all samples by the calculated offset
    for (const { centroid } of moveInfo.samplesWithCentroids) {
      await centroid.displace(latOffset, lonOffset);
    }

    dispatchMissionUpdated();
    // Targeted rebuild: only the moved samples' cores changed.
    const movedSampleIds = moveInfo.samplesWithCentroids.map(({ sample }) => sample.sample_id);
    dispatchSamplesUpdated(movedSampleIds);
    // centroid.displace moves waypoints — PathLayer needs to redraw.
    dispatchPathUpdated();
  }

  /**
   * Skip samples inside a polygon using SampleSkipper
   */
  private async skipSamplesInsidePolygon(mission: Mission, polygonPointsLatLon: Coordinate[][]): Promise<void> {
    const sampleSkipper = SampleSkipper.getInstance();

    await sampleSkipper.skipSamplesInsidePolygon(mission, polygonPointsLatLon);
  }

  /**
   * Unskip samples inside a polygon using SampleSkipper
   */
  private async unskipSamplesInsidePolygon(mission: Mission, polygonPointsLatLon: Coordinate[][]): Promise<void> {
    const sampleSkipper = SampleSkipper.getInstance();

    await sampleSkipper.unskipSamplesInsidePolygon(mission, polygonPointsLatLon);
  }

  /**
   * Remove (delete) samples and their cores inside a polygon
   */
  private async removeSamplesInsidePolygon(mission: Mission, polygonPointsLatLon: Coordinate[][]): Promise<void> {
    if (mission.is_zone_mission()) {
      return;
    }

    const removeCandidates = this.getSamplesInsidePolygon(mission, polygonPointsLatLon, {
      excludeWithBarcodes: true,
      excludeSkipped: false,
    });

    if (removeCandidates.length === 0) {
      return;
    }

    if (!(await alertConfirm(`Are you sure you want to remove ${removeCandidates.length} samples?`))) {
      return;
    }

    mission.delete_samples_and_cores(removeCandidates);
    // `delete_samples_and_cores` internally fires both
    // `dispatchMissionUpdated()` and `dispatchSamplesUpdated(...)`
    // with the deleted IDs.
  }

  /**
   * Clear barcodes for samples inside a polygon
   */
  private async clearBarcodesInsidePolygon(mission: Mission, polygonPointsLatLon: Coordinate[][]): Promise<void> {
    if (mission.is_zone_mission()) {
      return;
    }

    const clearCandidates = this.getSamplesInsidePolygon(mission, polygonPointsLatLon, {
      excludeWithBarcodes: false,
      excludeSkipped: false,
      onlyWithBarcodes: true,
    });

    if (clearCandidates.length === 0) {
      return;
    }

    if (!(await alertConfirm(`Are you sure you want to clear barcodes for ${clearCandidates.length} samples?`))) {
      return;
    }

    for (const sample of clearCandidates) {
      await sample.clearBarcode();
    }

    dispatchMissionUpdated();
    // Barcode cleared → CentroidsLayer needs to rebuild only the
    // cleared samples' centroid JSX (colour changes with bag_id).
    dispatchSamplesUpdated(clearCandidates.map((s) => s.sample_id));
  }

  /**
   * Prepare the move action by calculating the center and gathering samples
   * Returns information needed to complete the move after user selects new position
   */
  private async prepareMoveAction(
    mission: Mission,
    polygonPointsLatLon: Coordinate[][],
  ): Promise<MoveActionInfo | undefined> {
    if (mission.is_zone_mission()) {
      return;
    }

    const moveCandidates = this.getSamplesInsidePolygon(mission, polygonPointsLatLon, {
      excludeWithBarcodes: true,
      excludeSkipped: false,
    });

    if (moveCandidates.length === 0) {
      return;
    }

    // Gather samples with their centroids
    const samplesWithCentroids = moveCandidates
      .map((sample) => {
        const centroid = sample.getSampleSite()?.getSampleCentroid();

        if (!centroid) {
          return null;
        }

        return { sample, centroid };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    if (samplesWithCentroids.length === 0) {
      return;
    }

    // Calculate the center of all selected samples
    const center = samplesWithCentroids.reduce(
      (acc, { centroid }) => {
        return [acc[0] + centroid.lat, acc[1] + centroid.lon];
      },
      [0, 0],
    );

    const originalCenter: Coordinate = [
      center[0] / samplesWithCentroids.length,
      center[1] / samplesWithCentroids.length,
    ];

    return {
      action: 'Move',
      samplesWithCentroids,
      originalCenter,
    };
  }

  /**
   * Get samples inside a polygon with filtering options
   */
  private getSamplesInsidePolygon(
    mission: Mission,
    polygonPointsLatLon: Coordinate[][],
    options: {
      excludeWithBarcodes?: boolean;
      excludeSkipped?: boolean;
      onlyWithBarcodes?: boolean;
    } = {},
  ): Sample[] {
    const { excludeWithBarcodes = false, excludeSkipped = false, onlyWithBarcodes = false } = options;

    // turf needs "lon, lat", we receive "lat, lon"
    const poly = polygon(polygonPointsLatLon.map((ring) => ring.map((coordSet) => [coordSet[1], coordSet[0]])));

    return mission.getAllSamples().filter((sample) => {
      if (excludeWithBarcodes && sample.hasBarcode()) {
        return false;
      }

      if (excludeSkipped && sample.skipped_or_deleted) {
        return false;
      }

      if (onlyWithBarcodes && !sample.hasBarcode()) {
        return false;
      }

      const centroid = sample.getSampleSite()?.getSampleCentroid();

      if (!centroid) {
        return false;
      }

      const pt = point([centroid.lon, centroid.lat]);

      return booleanPointInPolygon(pt, poly);
    });
  }
}

export default LassoService;
